import {
  Button,
  ButtonColor,
  ButtonShape,
  CopyToClipboardButton,
  IconSize,
  Input,
  Loader,
  useDocumentTitle,
  useModalConfirmation,
  useToastManager
} from "@quortex/front-common"
import { useMutation, useQuery, useSuspenseQuery } from "@tanstack/react-query"
import { useState } from "react"
import { DefaultTTLValue } from "@/const"
import analyticsIllustration from "@/images/illustration-analytics.svg?url"
import { AverageBitrateForm } from "@/pages/ContentSteering/Settings/AverageBitrateForm"
import { OriginServerForm } from "@/pages/ContentSteering/Settings/OriginServerForm"
import { QoEProviderForm } from "@/pages/ContentSteering/Settings/QoEProviderForm"
import { QoEProviderModal } from "@/pages/ContentSteering/Settings/QoEProviderModal"
import type { QoEProviderModalGenerics } from "@/pages/ContentSteering/Settings/QoEProviderModal/QoEProviderModal"
import { TTLForm } from "@/pages/ContentSteering/Settings/TTLForm"
import {
  useDeleteQoEProviderConfig,
  useQoEProviderConfig,
  useUpdateQoeProviderConfig
} from "@/queries/qoe"
import { useWorkspaceList } from "@/queries/workspaces"

const Settings = () => {
  const {
    data: qoeProvider,
    isLoading,
    refetch: refetchQoeProvider
  } = useQuery({
    ...useQoEProviderConfig(),
    throwOnError: false
  })

  const [isEnableLoading, setIsEnableLoading] = useState(false)
  const { data: workspace } = useSuspenseQuery({
    ...useWorkspaceList(),
    refetchInterval: 5_000,
    select: d => d.results[0]
  })
  const { mutateAsync: updateQoeProvider } = useMutation(useUpdateQoeProviderConfig())
  const { mutateAsync: deleteQoEProvider } = useMutation(useDeleteQoEProviderConfig())
  const { openConfirmationModal } = useModalConfirmation()
  useDocumentTitle(`Settings - ${workspace?.name}`)

  const toastManager = useToastManager()

  const protocolLinks = workspace
    ? Object.entries(workspace.urls)
        .map(([protocol, url]) => ({
          protocol,
          url
        }))
        .sort((a, b) => (a.protocol > b.protocol ? 1 : -1))
    : []

  const onEnableChange = async () => {
    try {
      setIsEnableLoading(true)
      await updateQoeProvider({ enabled: !qoeProvider?.enabled })
      await refetchQoeProvider()
      setIsEnableLoading(false)
    } catch (e) {
      setIsEnableLoading(false)
      toastManager.error({ apiError: e })
    }
  }

  const onDisconnect = async () => {
    openConfirmationModal({
      cancelBtnDatatestId: "cancel-btn",
      message: "The provider configuration will be lost.\nQoE monitoring will be disabled.",
      okBtnDatatestId: "delete-btn",
      onAccept: async () => {
        try {
          await deleteQoEProvider()
          window.location.reload()
        } catch (e) {
          toastManager.error({ apiError: e })
        }
      },
      title: "Disconnect QoE provider?",
      variant: "destructive"
    })
  }

  return (
    <div className="flex flex-col">
      <OriginServerForm originServer={workspace?.originServer} />
      <div className="flex flex-col gap-sm">
        {protocolLinks.map(({ protocol, url }) => (
          <div
            key={protocol}
            className="radius-sm flex w-full items-center justify-between border border-grey-300 p-sm"
            data-testid={`${protocol}-url`}
          >
            <div className="typo-sm-medium">
              For {protocol === "hls" ? "HLS playlists" : "DASH manifests"}
            </div>
            <div>
              <div className="typo-sm-medium flex items-center gap-xs font-monospace text-blue-800">
                {url}
                <CopyToClipboardButton
                  color={ButtonColor.SECONDARY}
                  data-testid={`copy-url-button-${protocol}`}
                  iconSize={IconSize.MEDIUM}
                  shape={ButtonShape.UNCONTAIN}
                  valueToCopy={url}
                  withTitle={false}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
      <TTLForm ttl={workspace?.ttl || DefaultTTLValue} />
      <AverageBitrateForm workspace={workspace || undefined} />
      <div className="radius-sm relative mt-lg w-full items-center justify-between border border-grey-300 p-sm">
        {qoeProvider && (
          <>
            <div className="font-medium">
              <Input
                className="mr-xs"
                controlSize="small"
                data-testid="enabled-disabled-btn"
                defaultChecked={qoeProvider?.enabled}
                disabled={!qoeProvider || isEnableLoading}
                name="qoeMonitoring"
                type="checkbox"
                variant="toggle"
                onClick={onEnableChange}
              />
              QoE monitoring
            </div>
            <Button
              className="absolute top-sm right-sm"
              color={ButtonColor.DANGER_SECONDARY}
              data-testid="disconnect-btn"
              type="button"
              onClick={onDisconnect}
            >
              Disconnect
            </Button>
          </>
        )}

        <div className="mt-xs">
          {isLoading ? (
            <Loader />
          ) : qoeProvider ? (
            <div className="mt-md">
              <QoEProviderForm qoeProvider={qoeProvider} />
            </div>
          ) : (
            <div className="mt-md flex w-full flex-col items-center justify-center gap-sm">
              <img
                alt="QoE Monitoring"
                className="desktop:max-w-max max-w-[12.125rem]"
                src={analyticsIllustration}
              />
              <div className="text-grey-600">
                Connect your QoE provider to enable CDN health monitoring from the viewer’s
                perspective.
              </div>
              <Button
                color={ButtonColor.PRIMARY}
                data-testid="connect-qoe-provider-btn"
                to={{
                  search: old =>
                    ({
                      ...old,
                      modal: "qoe_provider"
                    }) satisfies QoEProviderModalGenerics["Search"]
                }}
                type="link"
              >
                Connect QoE provider
              </Button>
            </div>
          )}
        </div>
      </div>
      <QoEProviderModal />
    </div>
  )
}

export default Settings
